import { $t } from '@/i18n';
import { uuidv4 } from '@tencent/ui-core/lib/utils';
import eventBus, { EVENT } from '@utils/event-bus';
import { platformBuiltinModules } from '../../platform-builtin-modules';
import moduleRegistry from '../../module-registry';
const VUE_EXTERNAL_MODULES = ['vue', 'vue-router', 'pinia'];
const REACT_EXTERNAL_MODULES = ['react', 'react-dom', 'react-router-dom', 'react/jsx-runtime', 'react/jsx-dev-runtime', 'zustand', 'zustand/shallow'];
const PRESET_EXTERNALS = [
    ...Object.keys(platformBuiltinModules),
];
const DEFAULT_JIT_PACK_SERVER_URL = typeof LOCAL_JIT_PACK_SERVER_URL !== 'undefined'
    ? LOCAL_JIT_PACK_SERVER_URL
    : 'https://fdev.wuji.woa.com/gogo/';
const getJitPackServerUrl = () => window.GLOBAL_INFO?.jitPackServerUrl || DEFAULT_JIT_PACK_SERVER_URL;
const CALLER = 'window.npmLoader.register';
const EXTERNAL_RESOLVER = 'window._wuji_deps_loader';
/**
 * 远程 JIT Pack 打包器
 *
 * 只负责调用后台 SSE 流式接口进行 npm 安装与打包。
 * 模块注册、bundle 执行、外部解析器均由 ModuleRegistry 负责。
 */
class NpmLoader {
    packageJson = '';
    bundleFile = { js: '', css: '' };
    hash = '';
    loadedModules = new Set();
    externalModules = VUE_EXTERNAL_MODULES;
    loadPromiseResolveQueue = {};
    loadPromiseRejectQueue = {};
    flushQueueTimer = null;
    loadTaskQueue = [];
    setFramework(framework) {
        this.externalModules = framework === 'react' ? REACT_EXTERNAL_MODULES : VUE_EXTERNAL_MODULES;
    }
    /**
     * 打包指定依赖列表
     *
     * 多次调用会在 500ms 内合并为一次远程打包请求。
     * needCleanup=true 时清空已有依赖集合（用于 save 场景的全量重建）。
     */
    async bundle(dependencies, packageJson, needCleanup = false) {
        if (!dependencies.length) {
            return { bundle: { js: '', css: '' }, packageJson: '' };
        }
        if (packageJson !== undefined) {
            this.packageJson = packageJson;
        }
        const uuid = uuidv4();
        if (needCleanup) {
            this.loadedModules.clear();
        }
        dependencies.forEach(dep => this.loadedModules.add(dep));
        this.loadTaskQueue.push({ uuid, timestamp: Date.now() });
        if (this.flushQueueTimer) {
            clearTimeout(this.flushQueueTimer);
        }
        this.flushQueueTimer = setTimeout(() => {
            void this.flushQueue(uuid, needCleanup);
        }, 500);
        return new Promise((resolve, reject) => {
            this.loadPromiseResolveQueue[uuid] = resolve;
            this.loadPromiseRejectQueue[uuid] = reject;
        });
    }
    /**
     * 检查是否需要重新打包
     *
     * 比较目标依赖集合与当前已打包集合，若一致且 packageJson 未变则返回缓存结果。
     */
    checkNeedsRebuild(targetDeps, newPackageJson) {
        if (!targetDeps.length) {
            return { bundle: { js: '', css: '' }, packageJson: '' };
        }
        const hasBundled = Boolean(this.bundleFile.js || this.bundleFile.css);
        if (!hasBundled)
            return null;
        const currentDeps = Array.from(this.loadedModules).sort();
        const target = [...targetDeps].sort();
        const depsMatch = currentDeps.length === target.length
            && currentDeps.every((dep, i) => dep === target[i]);
        const pkgMatch = newPackageJson === undefined || newPackageJson === this.packageJson;
        if (depsMatch && pkgMatch) {
            return { bundle: this.bundleFile, packageJson: this.packageJson };
        }
        return null;
    }
    async getPackageJSON() {
        if (!this.hash) {
            return { name: '', version: '', private: false, dependencies: {} };
        }
        const serverUrl = getJitPackServerUrl();
        const response = await fetch(`${serverUrl}/package/${this.hash}`);
        return response.json();
    }
    async flushQueue(uuid, needCleanup = false) {
        if (!needCleanup) {
            const registeredModules = moduleRegistry.getModules();
            const needLoad = Array.from(this.loadedModules).filter(module => !registeredModules[module]);
            if (needLoad.length === 0) {
                this.resolveQueue(uuid, { bundle: this.bundleFile, packageJson: this.packageJson });
                return;
            }
        }
        try {
            const dependencies = Array.from(this.loadedModules);
            const bundleUrls = await this.bundleWithServer(dependencies);
            this.bundleFile = bundleUrls;
            await moduleRegistry.executeBundle(bundleUrls);
            const pkgJson = await this.getPackageJSON();
            this.packageJson = JSON.stringify(pkgJson);
            this.updateProgress({ stage: 'READY', success: true, message: $t('k_3059d24') });
            this.resolveQueue(uuid, { bundle: this.bundleFile, packageJson: this.packageJson });
        }
        catch (error) {
            console.error($t('k_2d329f7a'), error);
            this.updateProgress({ stage: 'BUNDLE', success: false, message: error.message });
            this.loadedModules.clear();
            this.rejectQueue(uuid, error);
        }
    }
    resolveQueue(uuid, result) {
        const taskIndex = this.loadTaskQueue.findIndex(task => task.uuid === uuid);
        const resolvedTasks = this.loadTaskQueue.splice(0, taskIndex + 1);
        resolvedTasks.forEach((task) => {
            this.loadPromiseResolveQueue[task.uuid]?.(result);
            delete this.loadPromiseResolveQueue[task.uuid];
            delete this.loadPromiseRejectQueue[task.uuid];
        });
    }
    rejectQueue(uuid, error) {
        const taskIndex = this.loadTaskQueue.findIndex(task => task.uuid === uuid);
        const rejectedTasks = this.loadTaskQueue.splice(0, taskIndex + 1);
        rejectedTasks.forEach((task) => {
            this.loadPromiseRejectQueue[task.uuid]?.(error);
            delete this.loadPromiseResolveQueue[task.uuid];
            delete this.loadPromiseRejectQueue[task.uuid];
        });
    }
    async bundleWithServer(dependencies) {
        const serverUrl = getJitPackServerUrl();
        this.updateProgress({
            stage: 'NPM_INSTALL',
            success: true,
            message: $t('k_b8ca50f6'),
        });
        const response = await fetch(`${serverUrl}/bundle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                packages: dependencies,
                caller: CALLER,
                externals: this.getExternals(),
                externalResolver: EXTERNAL_RESOLVER,
                packageJson: JSON.parse(this.packageJson || '{}'),
            }),
        });
        if (!response.ok) {
            throw new Error($t('k_2f23bb54', { arg_0: response.statusText }));
        }
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error($t('k_48d6a55e'));
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let completeResult = null;
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            let currentEvent = '';
            for (const line of lines) {
                if (line.startsWith('event:')) {
                    currentEvent = line.slice(6).trim();
                }
                else if (line.startsWith('data:')) {
                    const dataStr = line.slice(5).trim();
                    if (!dataStr)
                        continue;
                    try {
                        const data = JSON.parse(dataStr);
                        switch (currentEvent) {
                            case 'progress':
                                this.handleSSEProgress(data);
                                break;
                            case 'complete':
                                completeResult = data;
                                break;
                            case 'error':
                                throw new Error(data.message);
                        }
                    }
                    catch (e) {
                        if (e instanceof SyntaxError) {
                            console.warn($t('k_e0d38530'), dataStr);
                        }
                        else {
                            throw e;
                        }
                    }
                }
            }
        }
        if (!completeResult) {
            throw new Error($t('k_363a3033'));
        }
        const bundleUrls = { js: '', css: undefined };
        for (const file of completeResult.files) {
            const fullUrl = `${serverUrl}${file}`;
            if (file.endsWith('.js')) {
                bundleUrls.js = fullUrl;
            }
            else if (file.endsWith('.css')) {
                bundleUrls.css = fullUrl;
            }
        }
        this.hash = completeResult.hash;
        return bundleUrls;
    }
    handleSSEProgress(progress) {
        const mappedStage = ['PNPM_INSTALL', 'CACHE_CHECK'].includes(progress.stage) ? 'NPM_INSTALL' : progress.stage;
        this.updateProgress({
            stage: mappedStage,
            success: progress.success,
            message: progress.message,
        });
    }
    getExternals() {
        return [...new Set([...PRESET_EXTERNALS, ...this.externalModules])];
    }
    updateProgress(progress) {
        eventBus.emit(EVENT.updateDepLoaderState, progress);
    }
}
export default NpmLoader;
